import { Container, Divider, Link, MenuItem, MenuList, Theme, useMediaQuery } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import PaymentsIcon from '@mui/icons-material/Payments';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SettingsIcon from '@mui/icons-material/Settings';
import HomeIcon from '@mui/icons-material/Home';
import HelpIcon from '@mui/icons-material/Help';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { NavLink } from 'react-router-dom';
import logoutIconInverted from '../logoutIconInverted.svg';
import TuneIcon from '@mui/icons-material/Tune';
import { TenantAccount } from '../gql-types.generated';
import { RoutePath } from '../util/Routes';
import { fetchIsTenantMenuOpen } from '../features/app/AppSlice';
import { ApteanSSOProvider } from '../util/ApteanSSOProvider';
import { AUTH_MODE } from '../util/Constants';
import { b2cAuthProvider } from '../util/AuthProvider';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      textAlign: 'start',
    },
    userInfo: {
      padding: theme.spacing(3),
    },
    menuItem: {
      height: 55,
      textDecoration: 'none',
      color: theme.palette.text.primary,
      '&>.MuiDivider-root': {
        width: 'calc(100% - 12px)',
        position: 'absolute',
        top: 0,
        left: 0,
        marginLeft: 12,
      },
    },
    menuItemIcon: {
      marginRight: theme.spacing(2),
      color: theme.palette.uxGrey.main,
    },
    logoutIcon: {
      marginRight: theme.spacing(2),
      width: '1em',
      height: '1em',
      fontSize: '1.5rem',
      transform: 'rotate(180deg)',
    },
    manageAccountIcon: {
      marginRight: theme.spacing(2),
      width: '1em',
      height: '1em',
      fontSize: '1.5rem',
    },
    navList: {
      width: '100%',
      padding: 0,
    },
    contentWrap: {
      padding: 0,
    },
  }),
);
interface NavigationListProps {
  afterClick?: () => void;
  tenantAccount?: TenantAccount;
  showStatements: boolean;
  showPayoutReports: boolean;
  restrictedToUserManagement?: boolean;
}

const NavigationList: React.FC<NavigationListProps> = props => {
  const { afterClick, tenantAccount, showStatements, showPayoutReports, restrictedToUserManagement } = props;
  const dispatch = useDispatch();
  const classes = useStyles();
  const [isOnboarded, setIsOnboarded] = useState<boolean>(false);
  const [customerFeatureEnabled, setCustomerFeatureEnabled] = useState<boolean>(false);
  const matchesMdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const [isUserManager] = useState<boolean>(ApteanSSOProvider.userPermissions().canManageUsers);
  const navItemClick = () => {
    if (afterClick) {
      afterClick();
    }
    if (matchesMdDown) {
      dispatch(fetchIsTenantMenuOpen(false));
    }
  };

  useEffect(() => {
    if (tenantAccount) {
      // Update is onboarded & customer feature enabled states.
      setIsOnboarded(!!tenantAccount.tosAcceptance);
      setCustomerFeatureEnabled(tenantAccount?.settings?.features?.customers?.enabled || false);
    }
  }, [tenantAccount]);

  const handleLogout = () => {
    if (AUTH_MODE === 'b2c') {
      b2cAuthProvider?.logout();
    } else {
      ApteanSSOProvider.exitPortalApp();
    }
  };
  let signOutText = 'Exit';
  if (AUTH_MODE === 'b2c') {
    signOutText = 'Sign out';
  }

  if (restrictedToUserManagement) {
    return (
      <Container className={classes.contentWrap}>
        <MenuList className={classes.navList} data-cy="settings-menu">
          <MenuItem
            component={NavLink}
            onClick={navItemClick}
            className={classes.menuItem}
            to="/user-management"
            data-cy="manage-users"
          >
            <PersonIcon className={classes.menuItemIcon} />
            Manage Users
          </MenuItem>
        </MenuList>
      </Container>
    );
  }

  return (
    <Container className={classes.contentWrap}>
      <MenuList className={classes.navList} data-cy="settings-menu">
        <MenuItem component={Link} className={classes.menuItem} onClick={navItemClick} href={'/'} data-cy="nav-home">
          <HomeIcon className={classes.menuItemIcon} />
          Home
        </MenuItem>
        <MenuItem
          component={NavLink}
          className={classes.menuItem}
          onClick={navItemClick}
          to="/all-payments"
          data-cy="nav-all-payments"
        >
          <Divider />
          <PaymentsIcon className={classes.menuItemIcon} />
          Payments
        </MenuItem>
        <MenuItem
          component={NavLink}
          className={classes.menuItem}
          onClick={navItemClick}
          to="/payment-requests"
          data-cy="nav-payment-requests"
        >
          <Divider />
          <RequestQuoteIcon className={classes.menuItemIcon} />
          Payment Requests
        </MenuItem>
        {showStatements && (
          <MenuItem
            component={NavLink}
            className={classes.menuItem}
            onClick={navItemClick}
            to={RoutePath.Statements}
            data-cy="nav-statements"
          >
            <Divider />
            <FactCheckIcon className={classes.menuItemIcon} />
            Statements
          </MenuItem>
        )}
        {showPayoutReports && (
          <MenuItem
            component={NavLink}
            className={classes.menuItem}
            onClick={navItemClick}
            to={RoutePath.Reports}
            data-cy="nav-reports"
          >
            <Divider />
            <AssessmentIcon className={classes.menuItemIcon} />
            Reports
          </MenuItem>
        )}
        {isOnboarded && (
          <MenuItem component={NavLink} onClick={navItemClick} className={classes.menuItem} to="/settings" data-cy="manage-account">
            <Divider />
            <SettingsIcon className={classes.menuItemIcon} />
            Account Settings
          </MenuItem>
        )}
        {isOnboarded && (
          <MenuItem
            component={NavLink}
            onClick={navItemClick}
            className={classes.menuItem}
            to="/account-preferences"
            data-cy="account-preferences"
          >
            <Divider />
            <TuneIcon className={classes.menuItemIcon} />
            Account Preferences
          </MenuItem>
        )}
        {customerFeatureEnabled && (
          <MenuItem
            component={NavLink}
            onClick={navItemClick}
            className={classes.menuItem}
            to="/customer-management"
            data-cy="manage-customers"
          >
            <Divider />
            <PeopleIcon className={classes.menuItemIcon} />
            Manage Customers
          </MenuItem>
        )}
        {isOnboarded && isUserManager && (
          <MenuItem
            component={NavLink}
            onClick={navItemClick}
            className={classes.menuItem}
            to="/user-management"
            data-cy="manage-users"
          >
            <Divider />
            <PersonIcon className={classes.menuItemIcon} />
            Manage Users
          </MenuItem>
        )}
        <MenuItem component={NavLink} className={classes.menuItem} onClick={navItemClick} to="/help" data-cy="help">
          <Divider />
          <HelpIcon className={classes.menuItemIcon} />
          Help
        </MenuItem>
        <MenuItem className={classes.menuItem} onClick={handleLogout} data-cy="sign-out">
          <Divider />
          <img className={classes.logoutIcon} alt="logout" src={logoutIconInverted} />
          {signOutText}
        </MenuItem>
      </MenuList>
    </Container>
  );
};

export default NavigationList;
